import { create } from 'zustand'
import type { CallView, UserView } from '@olmt/shared'
import { shouldDismissRing, type DismissedRings, type RingDismissal } from '../lib/callRules'

export type ActiveCall = {
  call: CallView
  token: string
  /** Signalling base from the api response; fall back to VITE_LIVEKIT_URL when empty. */
  wsUrl: string
}

/**
 * The caller as the incoming screen needs them: `call.ring`'s `caller` view (CONTRACT §5) or the
 * SFU's participant view.
 */
export type IncomingCaller = Pick<UserView, 'displayName' | 'avatarUrl'>

export type IncomingCall = {
  call: CallView
  caller: IncomingCaller | null
  /** Set when the ring came from a push notification / deep link rather than the live socket. */
  fromPush: boolean
}

/** Lifecycle of the overlay: idle → prejoin (device check) → connecting → connected. */
export type CallPhase = 'idle' | 'prejoin' | 'connecting' | 'connected'

interface CallState {
  active: ActiveCall | null
  incoming: IncomingCall | null
  /**
   * W7: call ids whose ring this device has taken down on a sibling device's say-so, and why. A
   * ring stopped this way leaves nothing behind but `incoming: null`, which is the same state as a
   * ring that has not arrived yet — `IncomingCallRoute` raises a ring again from a fetch on every
   * pass, so without this memory the sibling's answer would undo itself there. Read by that route
   * alone; never cleared, because a ring for the same call raised again (`POST /calls/:id/ring`)
   * sets `incoming` and the route prefers the live ring over this record.
   */
  dismissedRings: DismissedRings
  /** Live in the native host, not in this WebView. Cleared by the same signals that end a call. */
  /**
   * A line about a call the user did not end themselves — "Yousef declined the call" — so a call
   * that stops without them saying so is not silent. Cleared when the next call starts.
   */
  notice: string | null
  phase: CallPhase
  error: string | null
  setActive: (call: ActiveCall) => void
  clearActive: () => void
  setIncoming: (call: IncomingCall | null) => void
  /**
   * W7: `call.dismiss` — another of this user's devices answered or declined `callId`, so the ring
   * this device is showing for it comes down. Anything else is a no-op: the frame reaches every
   * socket the user owns, the device that acted included, and a device ringing a different call
   * must not be touched. Returns whether this device's ring came down, so the caller can clear the
   * native ring notification with it.
   */
  dismissRing: (dismissal: RingDismissal) => boolean
  setNotice: (text: string) => void
  clearNotice: () => void
  setPhase: (phase: CallPhase) => void
  setError: (error: string | null) => void
}

export const useCallStore = create<CallState>()((set, get) => ({
  active: null,
  incoming: null,
  dismissedRings: {},
  notice: null,
  phase: 'idle',
  error: null,
  setActive: (active) => set({ active, incoming: null, notice: null, phase: 'prejoin', error: null }),
  clearActive: () => set({ active: null, phase: 'idle', error: null }),
  setIncoming: (incoming) => (incoming ? set({ incoming, notice: null }) : set({ incoming })),
  // The same `incoming: null` an answer or a decline performs here, so the ring tone and the ring
  // surface come down through the one path they already have. `shouldDismissRing` is the whole
  // decision; the record is written with the clear, and only a device that was really ringing that
  // call writes one — the acting device leaves no trace here either.
  dismissRing: ({ callId, reason }) => {
    if (!shouldDismissRing(get().incoming, callId)) return false
    set((state) => ({
      incoming: null,
      dismissedRings: { ...state.dismissedRings, [callId]: reason },
    }))
    return true
  },
  // Like `setActive`: taking a call clears the ring, including the frame the server echoes back to
  // the caller's own device.
  setNotice: (notice) => set({ notice }),
  clearNotice: () => set({ notice: null }),
  setPhase: (phase) => set({ phase }),
  setError: (error) => set({ error }),
}))
